import getDealTypes from 'components/common/Deal/CampaignHeader/CampaignDrawer/Details/SelectCampaignType/getDealTypes/getDealTypes';
import { DealType, DealTypeShortLabel, DealTypeValueLabel } from 'components/common/types/Deal.types';
import { FeatureFlags } from 'components/common/types/Features.types';
import { Store } from 'components/common/types/Store.types';
import { PermissionsEnum } from 'consts/permissions';
import { useHasFeatureAccess } from 'customHooks/useHasFeatureAccess';
import ButtonGroup from 'lib/ButtonGroup';
import { Height, Theme, Width } from 'lib/ButtonGroup/ButtonGroup.types';
import Auth from 'modules/Auth';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { changeDealType } from 'store/dealManagement/actions';
import { changeDealCurrentLineData, getDefaultDealType } from 'store/dealManagement/reducer';
import { getDealFormReadOnly } from 'store/dealManagement/selectors/getDealFormReadOnly';
import { getDealType } from 'store/dealManagement/selectors/getDealType';
import { getIsNewDeal } from 'store/dealManagement/selectors/getIsNewDeal';
import { getIsReseller } from 'store/publisher/selectors';
import { getDefaultSweep } from 'utils/deal';
import usePendoTracking from 'customHooks/usePendoTracking';

export const SelectCampaignType = (): React.ReactElement | null => {
  const dispatch = useDispatch();

  const hasNonGuaranteedExtendedTargetEnabled = useHasFeatureAccess(FeatureFlags.NON_GUARANTEED_EXTENDED_TARGET);

  const isReseller = useSelector(getIsReseller);
  const isNewDeal = useSelector(getIsNewDeal);
  const publisherType = useSelector((state: Store) => state.publisher.configuration.type);
  const dealType = useSelector(getDealType) || getDefaultDealType(publisherType);
  const dealStatus = useSelector((state: Store) => state.dealManagement.commonDeal.bookingStatusCode);
  const readOnly = useSelector(getDealFormReadOnly);
  const { getPendoDataId } = usePendoTracking();

  const dealTypesList = getDealTypes({ dealType, isNewDeal, publisherType, dealStatus });

  const options = dealTypesList.map((type) => ({
    text: DealTypeShortLabel[type.code],
    value: type.code,
    tooltip: DealTypeValueLabel[type.code],
    dataPendoId: getPendoDataId(`Deal type ${DealTypeShortLabel[type.code]}`),
  }));

  const handleSelectCampaignType = (selectedCampaignType: DealType): void => {
    dispatch(changeDealType(selectedCampaignType, hasNonGuaranteedExtendedTargetEnabled));
    dispatch(
      changeDealCurrentLineData({
        sweep: getDefaultSweep(selectedCampaignType, hasNonGuaranteedExtendedTargetEnabled),
      }),
    );
  };

  useEffect(() => {
    if (isReseller) {
      dispatch(
        changeDealCurrentLineData({
          sweep: getDefaultSweep(dealType, hasNonGuaranteedExtendedTargetEnabled),
        }),
      );
    }
  }, [isReseller, dealType, hasNonGuaranteedExtendedTargetEnabled]);

  return Auth.hasPermission(PermissionsEnum.DEAL_TYPE_VIEW) ? (
    <ButtonGroup
      onChange={handleSelectCampaignType}
      options={options}
      selectedValue={dealType}
      theme={Theme.SUBTLE}
      width={Width.MAX}
      height={Height.MEDIUM}
      isDisabled={readOnly}
      dataTestId="campaign-type-toggle"
      testIdSource="text"
    />
  ) : null;
};
